import { supabase } from '../lib/supabase';

export interface Alert {
  id: string;
  name: string;
  whatsapp: string;
  origin: string;
  destination: string;
  message: string | null;
  send: boolean;
  created_at: string;
}

export interface Registration {
  id: string;
  name: string;
  whatsapp: string;
  origin_city: string;
  max_price?: number;
  destination_cities: Array<{
    id: string;
    city_name: string;
  }>;
}

export interface RegistrationSummary {
  origin_city: string;
  destination_cities: string[];
  registration_count: number;
}

export async function getRegistrations(): Promise<Registration[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('registrations')
      .select(`
        *,
        destination_cities (
          id,
          city_name
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching registrations:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getRegistrations:', error);
    throw new Error('Erro ao carregar registros!');
  }
}

export async function getGroupedRegistrations(): Promise<RegistrationSummary[]> {
  try {
    const { data, error } = await supabase
      .from('registrations_summary')
      .select('*');

    if (error) {
      console.error('Error fetching grouped registrations:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error in getGroupedRegistrations:', error);
    throw new Error('Erro ao carregar resumo dos registros!');
  }
}

export async function deleteAlert(alertId: string): Promise<void> {
  try {
    // Verificar se o usuário está autenticado
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Usuário não autenticado');
    }

    // Verificar se o alerta pertence ao usuário atual
    const { data: alert, error: fetchError } = await supabase
      .from('registrations')
      .select('user_id')
      .eq('id', alertId)
      .single();

    if (fetchError) throw fetchError;
    if (!alert) throw new Error('Alerta não encontrado');
    if (alert.user_id !== user.id) throw new Error('Você não tem permissão para excluir este alerta');

    // Excluir o alerta
    const { error } = await supabase
      .from('registrations')
      .delete()
      .eq('id', alertId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting alert:', error);
    throw error instanceof Error ? error : new Error('Erro ao excluir alerta!');
  }
}

export async function createAlertsFromOpportunity(
  origin: string,
  destination: string,
  departureDates: Date[],
  returnDates: Date[],
  value: number,
  feesIncluded: boolean
): Promise<Alert[]> {
  try {
    // Get registrations that match the opportunity criteria
    const { data: registrations, error: registrationsError } = await supabase
      .from('registrations')
      .select(`
        id,
        name,
        whatsapp,
        origin_city,
        max_price,
        destination_cities (
          city_name
        )
      `)
      .eq('origin_city', origin);

    if (registrationsError) throw registrationsError;

    if (!registrations || registrations.length === 0) {
      return [];
    }

    const alerts: Alert[] = [];

    // Create alerts for matching registrations
    for (const registration of registrations) {
      // Check if destination matches any of the user's desired destinations
      const destinationMatches = registration.destination_cities.some(
        dc => dc.city_name === destination
      );

      // Check if price is within user's max price (if specified)
      const priceMatches = !registration.max_price || value <= registration.max_price;

      if (destinationMatches && priceMatches) {
        // Format dates for message
        const formattedDepartureDates = departureDates
          .map(date => new Date(date).toLocaleDateString('pt-BR'))
          .join(', ');
        const formattedReturnDates = returnDates
          .map(date => new Date(date).toLocaleDateString('pt-BR'))
          .join(', ');

        // Create message
        const message = `🎯 Nova oportunidade encontrada!\n\n` +
          `✈️ ${origin} → ${destination}\n` +
          `📅 Ida: ${formattedDepartureDates}\n` +
          `🔄 Volta: ${formattedReturnDates}\n` +
          `💰 Valor: R$ ${value.toFixed(2)}` +
          `${feesIncluded ? ' (taxas inclusas)' : ''}\n\n` +
          `Acesse nosso site para mais detalhes!`;

        // Insert alert
        const { data: alert, error: insertError } = await supabase
          .from('alerts')
          .insert({
            name: registration.name,
            whatsapp: registration.whatsapp,
            origin,
            destination,
            message,
            send: false
          })
          .select()
          .single();

        if (insertError) {
          console.error('Error creating alert:', insertError);
          continue;
        }

        if (alert) {
          alerts.push(alert);
        }
      }
    }

    return alerts;
  } catch (error) {
    console.error('Error creating alerts from opportunity:', error);
    throw new Error('Erro ao criar alertas para a oportunidade');
  }
}